import {useQuery} from '@tanstack/react-query';
import {
  BillingBlock, BillingBlockDispatch, BillingBlockExemption, BillingBlockRun, DelinquencySettings,
} from 'src/core/data/api/BillingBlock';
import BillingService from 'src/services/billing-service';
import {getCurrentCustomerId} from 'src/helpers/customer';
import {BlocksFilter} from './provider';
import {useEntityOptions} from './entities';

export const billingBlockKeys = {
  all: ['billingBlock'] as const,
  settings: () => [...billingBlockKeys.all, 'settings'] as const,
  blocks: () => [...billingBlockKeys.all, 'blocks'] as const,
  blocksFiltered: (filter: BlocksFilter) => [...billingBlockKeys.blocks(), filter] as const,
  exemptions: () => [...billingBlockKeys.all, 'exemptions'] as const,
  runs: () => [...billingBlockKeys.all, 'runs'] as const,
  dispatches: (runId: number) => [...billingBlockKeys.all, 'dispatches', runId] as const,
};

export const useDelinquencySettings = () => {
  const service = new BillingService();
  return useQuery<DelinquencySettings>({
    queryKey: billingBlockKeys.settings(),
    queryFn: () => service.getDelinquencySettings(getCurrentCustomerId()),
    retry: false,
  });
};

const byNewest = <T, >(rows: T[], field: (row: T) => string) => (
  [...rows].sort((a, b) => new Date(field(b)).getTime() - new Date(field(a)).getTime())
);

export const useBillingBlocks = (filter: BlocksFilter, enabled = true) => {
  const service = new BillingService();
  return useQuery<BillingBlock[]>({
    enabled,
    queryKey: billingBlockKeys.blocksFiltered(filter),
    queryFn: async () => {
      const rows: BillingBlock[] = await service.listBlocks(getCurrentCustomerId(), {
        status: filter.status === 'ALL' ? undefined : filter.status,
        customerId: filter.customerId || undefined,
        deviceId: filter.deviceId || undefined,
      });
      return byNewest(rows || [], (row) => row.blockedAt);
    },
    retry: false,
  });
};

export interface ExemptionRow extends BillingBlockExemption {
  kind: 'customer' | 'device';
  targetId: number;
  name: string | null;
}

// Exemptions come without names; they are resolved from the lists the module already loads.
export const useBlockExemptions = () => {
  const service = new BillingService();
  const {customers, devices} = useEntityOptions();
  const query = useQuery<BillingBlockExemption[]>({
    queryKey: billingBlockKeys.exemptions(),
    queryFn: () => service.listBlockExemptions(getCurrentCustomerId()),
    retry: false,
  });
  const rows: ExemptionRow[] = (query.data || []).map((exemption) => {
    const kind = exemption.customerId !== 0 ? 'customer' : 'device';
    const targetId = kind === 'customer' ? exemption.customerId : exemption.deviceId;
    const name = (kind === 'customer' ? customers : devices).find((option) => option.id === targetId)?.label ?? null;
    return {...exemption, kind, targetId, name};
  });
  return {...query, rows};
};

export const useBlockRuns = () => {
  const service = new BillingService();
  return useQuery<BillingBlockRun[]>({
    queryKey: billingBlockKeys.runs(),
    queryFn: async () => byNewest((await service.listBlockRuns(getCurrentCustomerId())) || [], (run: BillingBlockRun) => run.startedAt),
    retry: false,
  });
};

// Dispatches tell the sequence of one run, so they read oldest first.
export const useBlockRunDispatches = (runId: number) => {
  const service = new BillingService();
  return useQuery<BillingBlockDispatch[]>({
    queryKey: billingBlockKeys.dispatches(runId),
    queryFn: async () => byNewest(
        (await service.listBlockRunDispatches(getCurrentCustomerId(), runId)) || [],
        (dispatch: BillingBlockDispatch) => dispatch.createdAt,
    ).reverse(),
    retry: false,
  });
};
